"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MARKER = exports.ZOOM_LEVELS = exports.DEFAULT_ZOOM = void 0;
exports.getNearbyPatientsLocationData = getNearbyPatientsLocationData;
exports.getGroupPatientStatusColor = getGroupPatientStatusColor;
exports.isAddressEmpty = isAddressEmpty;
exports.getAdvanceSearchFilters = getAdvanceSearchFilters;
exports.getPatientHeaderProps = getPatientHeaderProps;
exports.getZoomLevelForDistance = getZoomLevelForDistance;
exports.getLatitudeDeltaForDistance = getLatitudeDeltaForDistance;
exports.getLongitudeDeltaFromLatitudeDelta = getLongitudeDeltaFromLatitudeDelta;
const domain_1 = require("@home-health-notify/domain");
const AdvancedSearchHelper_1 = require("./AdvancedSearchHelper");
const LayoutHelper_1 = require("./LayoutHelper");
/**
 * Default zoom level for maps
 */
exports.DEFAULT_ZOOM = 15;
/**
 * Zoom levels mapped to distance in miles
 * These values work for both Google Maps (web) and react-native-maps (mobile)
 */
exports.ZOOM_LEVELS = {
    [AdvancedSearchHelper_1.DISTANCE_IN_MILES.TWO]: 13.9,
    [AdvancedSearchHelper_1.DISTANCE_IN_MILES.FIVE]: 12.5,
    [AdvancedSearchHelper_1.DISTANCE_IN_MILES.TEN]: 11.5,
    [AdvancedSearchHelper_1.DISTANCE_IN_MILES.FIFTEEN]: 11,
    [AdvancedSearchHelper_1.DISTANCE_IN_MILES.TWENTY]: 10.6,
    [AdvancedSearchHelper_1.DISTANCE_IN_MILES.TWENTY_FIVE]: 10.3,
};
/**
 * Marker types for advanced search map
 */
var MARKER;
(function (MARKER) {
    MARKER["PATIENT_ADDRESS"] = "patientAddress";
    MARKER["CLINICIAN"] = "clinician";
    MARKER["NEARBY_PATIENT"] = "nearbyPatient";
})(MARKER || (exports.MARKER = MARKER = {}));
/**
 * Groups nearby patients by their coordinates
 * Useful for rendering clustered markers on the map
 * @param nearbyPatients - Array of nearby patients
 * @returns Object with coordinates as keys and patient arrays as values
 */
function getNearbyPatientsLocationData(nearbyPatients) {
    const patientsLocationData = {};
    nearbyPatients.forEach((patient) => {
        const [longitude, latitude] = patient.location.coordinates;
        const latLng = `${latitude},${longitude}`;
        if (patientsLocationData[latLng]) {
            patientsLocationData[latLng].push(patient);
        }
        else {
            patientsLocationData[latLng] = [patient];
        }
    });
    return patientsLocationData;
}
/**
 * Gets the status color for a group of patients at the same location
 * @param patients - Array of nearby patients at the same location
 * @param mixedStatusColor - Color to use when patients have different statuses
 * @returns StatusColor object with iconColor and borderColor
 */
function getGroupPatientStatusColor(patients, mixedStatusColor) {
    const firstPatientStatus = patients[0].patientStatus;
    const statusProperty = (0, LayoutHelper_1.getGroupStatusProperty)(firstPatientStatus);
    let statusColor = {
        iconColor: statusProperty.upperBarColor,
        borderColor: statusProperty.lowerBarColor,
    };
    if (patients.length > 1) {
        for (const patient of patients) {
            if (patient.patientStatus !== firstPatientStatus) {
                statusColor = {
                    iconColor: mixedStatusColor,
                    borderColor: mixedStatusColor,
                };
                break;
            }
        }
    }
    else if (firstPatientStatus === 5) {
        // Pending ROC status
        return {
            iconColor: `linear-gradient(to right, ${statusProperty.upperBarColor} 0%, ${statusProperty.upperBarColor} 50%, ${statusProperty.lowerBarColor} 50%, ${statusProperty.lowerBarColor} 100%)`,
            borderColor: statusProperty.upperBarColor,
        };
    }
    return statusColor;
}
/**
 * Checks if the advance search filters have an empty/invalid address
 * @param advanceSearchFilters - The advance search filters
 * @returns true if address is empty or invalid
 */
function isAddressEmpty(advanceSearchFilters) {
    return (!advanceSearchFilters ||
        (advanceSearchFilters.lat === 0 && advanceSearchFilters.lng === 0));
}
/**
 * Extracts advance search filters from group info
 * @param groupInfo - The group info object
 * @returns IAdvanceSearchFilter or undefined if not a patient group
 */
function getAdvanceSearchFilters(groupInfo) {
    var _a;
    const address = (_a = groupInfo.patientInfo) === null || _a === void 0 ? void 0 : _a.address;
    if (groupInfo.groupType === domain_1.GROUP_TYPE.PATIENT && address) {
        return {
            lat: address.lat,
            lng: address.lng,
            chatId: groupInfo.id,
        };
    }
    return undefined;
}
/**
 * Extracts patient header props from group info
 * @param groupInfo - The group info object
 * @returns IPatientHeaderProps or undefined if not a patient group
 */
function getPatientHeaderProps(groupInfo) {
    var _a;
    if (groupInfo.groupType !== domain_1.GROUP_TYPE.PATIENT) {
        return undefined;
    }
    const patientGroupInfo = groupInfo;
    return {
        meta: patientGroupInfo.meta,
        name: patientGroupInfo.name,
        patientStatus: patientGroupInfo.patientStatus,
        servicesToPatient: patientGroupInfo.servicesToPatient,
        address: (_a = patientGroupInfo.patientInfo) === null || _a === void 0 ? void 0 : _a.address,
    };
}
/**
 * Gets the zoom level for a given distance
 * @param distance - Distance in miles
 * @returns Zoom level for the map
 */
function getZoomLevelForDistance(distance) {
    var _a;
    return (_a = exports.ZOOM_LEVELS[distance]) !== null && _a !== void 0 ? _a : exports.DEFAULT_ZOOM;
}
/**
 * Calculates the latitude delta for react-native-maps based on distance
 * This is used to set the map region zoom level
 * @param distanceInMiles - Distance in miles
 * @returns Latitude delta for MapView region
 */
function getLatitudeDeltaForDistance(distanceInMiles) {
    // Approximate conversion: 1 degree of latitude = ~69 miles
    // We multiply by 2.5 to show a bit more area around the circle
    return (distanceInMiles / 69) * 2.5;
}
/**
 * Calculates the longitude delta based on latitude delta and aspect ratio
 * @param latitudeDelta - The latitude delta
 * @param aspectRatio - Map aspect ratio (default 1 for square)
 * @returns Longitude delta for MapView region
 */
function getLongitudeDeltaFromLatitudeDelta(latitudeDelta, aspectRatio = 1) {
    return latitudeDelta * aspectRatio;
}
